/**
 * AuthDiagnose — public self-serve diagnostic for users who can't sign in.
 * Runs a series of read-only checks (cookie present? bearer token in
 * localStorage? backend reachable? /auth/me result?) and surfaces the
 * results so a stuck user knows what to do next instead of staring at a
 * blank login screen.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldQuestion, CheckCircle2, XCircle, AlertTriangle, Loader2, ArrowLeft, Mail, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "maxsc_session_token";

export default function AuthDiagnose() {
    const { clearStuckSession } = useAuth();
    const [checks, setChecks] = useState([]);
    const [busy, setBusy] = useState(true);

    const runChecks = async () => {
        setBusy(true);
        const next = [];

        // 1) Backend reachability
        try {
            const r = await axios.get(`${API}/`);
            next.push({ id: "backend", label: "Backend reachable", ok: r.status === 200, detail: `${r.status} OK` });
        } catch (e) {
            next.push({ id: "backend", label: "Backend reachable", ok: false, detail: e.message });
        }

        // 2) Smoke test
        try {
            const r = await axios.get(`${API}/health/auth/smoke`);
            next.push({
                id: "smoke", label: "Sign-in smoke test", ok: r.data.ok,
                detail: r.data.ok ? `${r.data.total_ms}ms · all 3 steps passed` : `Failed: ${r.data.error || "unknown"}`,
            });
        } catch (e) {
            next.push({ id: "smoke", label: "Sign-in smoke test", ok: false, detail: e.message });
        }

        // 3) Cookie present?
        const hasCookie = document.cookie.split(";").some((c) => c.trim().startsWith("session_token="));
        next.push({
            id: "cookie", label: "Session cookie present", ok: hasCookie,
            detail: hasCookie ? "Found session_token cookie" : "No session_token cookie (Safari ITP often strips it)",
        });

        // 4) localStorage bearer fallback?
        let bearer = null;
        try { bearer = window.localStorage.getItem(TOKEN_KEY); } catch { /* noop */ }
        next.push({
            id: "bearer", label: "Bearer token fallback present", ok: !!bearer,
            detail: bearer ? `Yes (${bearer.slice(0, 8)}…)` : "No — sign in once to populate it",
        });

        // 5) /auth/me — am I actually signed in?
        try {
            const headers = bearer ? { Authorization: `Bearer ${bearer}` } : {};
            const r = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
            next.push({
                id: "me", label: "/auth/me succeeds", ok: true,
                detail: `Signed in as ${r.data.email} (role: ${r.data.role})`,
            });
        } catch (e) {
            next.push({
                id: "me", label: "/auth/me succeeds", ok: false,
                detail: e?.response?.status === 401 ? "Not signed in (401)" : (e.message || "Unknown error"),
            });
        }

        setChecks(next);
        setBusy(false);
    };

    useEffect(() => { runChecks(); }, []);

    const allPass = checks.length > 0 && checks.every((c) => c.ok);
    const someAuthFail = checks.find((c) => c.id === "me")?.ok === false;

    return (
        <div data-testid="auth-diagnose" className="min-h-screen bg-brand-obsidian text-white">
            <div className="max-w-2xl mx-auto px-6 py-12">
                <Link to="/" className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
                    <ArrowLeft className="w-3 h-3" /> Home
                </Link>
                <h1 className="font-display text-4xl tracking-wider text-white mt-3 mb-1 flex items-center gap-3">
                    <ShieldQuestion className="w-7 h-7 text-brand-red" /> Stuck signing in?
                </h1>
                <p className="text-sm text-zinc-300 mb-6">
                    This page checks every step in the sign-in chain so you can see what's actually broken — and what to do about it.
                </p>

                <button
                    type="button"
                    onClick={runChecks}
                    disabled={busy}
                    data-testid="auth-diagnose-rerun"
                    className="btn-ghost-tactical text-xs inline-flex items-center gap-2 mb-5"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run checks again
                </button>

                <div className="space-y-2 mb-6">
                    {checks.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`diag-${c.id}`}
                            className={`border rounded-lg p-3 ${c.ok ? "border-emerald-800 bg-emerald-950/15" : "border-red-800 bg-red-950/15"}`}
                        >
                            <div className="flex items-start gap-3">
                                {c.ok
                                    ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                                    : <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                                }
                                <div className="flex-1">
                                    <div className={`text-sm font-bold ${c.ok ? "text-emerald-200" : "text-red-200"}`}>{c.label}</div>
                                    <div className="text-[12px] text-zinc-300 mt-0.5">{c.detail}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {checks.length > 0 && (
                    <div className="border border-zinc-800 bg-zinc-950 rounded-lg p-5">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-2">What now?</div>
                        {allPass ? (
                            <p className="text-sm text-emerald-300">
                                <CheckCircle2 className="w-4 h-4 inline mr-1" /> Everything passes. You should be signed in — try going to <Link to="/dashboard" className="underline">/dashboard</Link>.
                            </p>
                        ) : (
                            <ul className="text-sm text-zinc-200 space-y-2 list-disc pl-5">
                                {someAuthFail && (
                                    <>
                                        <li>You're not currently signed in. Try the email sign-in option — it's the most reliable.</li>
                                        <li>
                                            <button
                                                type="button"
                                                onClick={async () => { await clearStuckSession(); window.location.href = "/"; }}
                                                data-testid="auth-diagnose-clear"
                                                className="text-amber-300 hover:text-white underline inline-flex items-center gap-1.5"
                                            >
                                                <AlertTriangle className="w-3.5 h-3.5" /> Clear any stale session and start fresh
                                            </button>
                                        </li>
                                    </>
                                )}
                                <li>
                                    <Link to="/" className="text-emerald-300 hover:text-white underline inline-flex items-center gap-1.5">
                                        <Mail className="w-3.5 h-3.5" /> Use email sign-in from the home page
                                    </Link>
                                </li>
                                <li>If checks above keep failing, contact your coach — they have an owner-only "re-issue session" tool that works even when nothing else does.</li>
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
